import { Alert, Button, Group, Tabs } from "@mantine/core";
import { useEffect } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { FormFieldController } from "../../../../../../components/BuildingBlocks/Forms/Controllers/FormFieldController";
import { setErrorsFromResponseData } from "../../../../../../components/BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { HistoryTab } from "../../../../../../components/HistoryTab/HistoryTab";
import { LoadOrError } from "../../../../../../components/LoadOrError/LoadOrError";
import { useMeteringOrMarketLocations } from "../../../../../../hooks/useMeteringOrMarketLocations";
import { AlertPresets } from "../../../../../../mantine/mantinePresets";
import { useToast } from "../../../../../../mantine/notifications/useToast";
import { GeneratorType } from "../../../../../../types/api.types";
import { buildFieldNameToLabelMap } from "../../../../../../utils/react-hook-form/buildFieldNameToLabelMap";
import type {
  Paragraph6GemeindeAnteilErzeugerPayload,
  Paragraph6GemeindeAnteilErzeugerResponse,
  Paragraph6GeneratorListElement
} from "../../../../Paragraph6.types";
import { BlockingErrorAlert } from "./BlockingErrorAlert";
import {
  PARAGRAPH_6_GENERATOR_FORM_INPUT_DATA,
  TABS
} from "./Paragraph6GeneratorForm.constants";
import "./Paragraph6GeneratorForm.scss";

const fieldNameToLabelMap = buildFieldNameToLabelMap(
  PARAGRAPH_6_GENERATOR_FORM_INPUT_DATA
);

interface Paragraph6GeneratorFormProps {
  projectId: string;
  editMode: boolean;
  paragraph6GemeindeAnteilErzeuger?: Partial<Paragraph6GemeindeAnteilErzeugerResponse>;
  paragraph6UnassignedGenerators?: Array<Paragraph6GeneratorListElement>;
  isSubmitting?: boolean;
  submissionError?: Error;
  onSubmit: SubmitHandler<Paragraph6GemeindeAnteilErzeugerPayload>;
  onCancel: () => void;
}

export const COPIED_FROM_NOTIFICATION =
  "Für den ausgewählten Erzeuger wurden bereits vorhandene Daten übernommen.";

function Paragraph6GeneratorForm({
  projectId,
  editMode,
  paragraph6GemeindeAnteilErzeuger,
  paragraph6UnassignedGenerators,
  isSubmitting,
  submissionError,
  onSubmit,
  onCancel
}: Paragraph6GeneratorFormProps) {
  const { showToast, showErrorToast } = useToast();
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    setError,
    watch
  } = useForm<Paragraph6GemeindeAnteilErzeugerResponse>({
    defaultValues: paragraph6GemeindeAnteilErzeuger ?? {}
  });

  const generatorValue = watch("generator");
  const selectedGenerator = paragraph6UnassignedGenerators?.find(
    (unassignedGenerator) => unassignedGenerator.generator === generatorValue
  );

  const installierteLeistungMissingError =
    generatorValue && !selectedGenerator?.installed_capacity
      ? t("errors.Paragraph6.GeneratorMissingInstalledCapacity")
      : undefined;
  const installierteLeistungTooLowWarning =
    generatorValue &&
    selectedGenerator?.generator_type === GeneratorType.EEGAnlageWind &&
    selectedGenerator?.installed_capacity &&
    selectedGenerator.installed_capacity <= 1000
      ? t("errors.Paragraph6.GeneratorInstalledCapacityTooLow", {
          capacity: selectedGenerator.installed_capacity
        })
      : undefined;
  const anlagenschluesselError =
    generatorValue && !selectedGenerator?.anlagenschluessel_eeg
      ? t("errors.Paragraph6.GeneratorMissingAnlagenschluessel")
      : undefined;
  const weightingError =
    generatorValue && !selectedGenerator?.weighting
      ? t("errors.Paragraph6.GeneratorMissingWeighting")
      : undefined;
  const hasBlockingError =
    selectedGenerator &&
    (!!installierteLeistungMissingError ||
      !!anlagenschluesselError ||
      !!weightingError);

  const {
    data: meteringOrMarketLocations,
    isLoading: meteringOrMarketLocationsLoading
  } = useMeteringOrMarketLocations({
    siteId: editMode
      ? paragraph6GemeindeAnteilErzeuger?.site
      : selectedGenerator?.site
  });

  const generatorChoices = getGeneratorChoices();
  const generatorFieldInfo = {
    ...PARAGRAPH_6_GENERATOR_FORM_INPUT_DATA.generator,
    data: generatorChoices,
    disabled: editMode
  };

  function getGeneratorChoices(): Array<{ value: number; label: string }> {
    if (editMode && paragraph6GemeindeAnteilErzeuger?.generator_display_name) {
      return [
        {
          value: paragraph6GemeindeAnteilErzeuger.generator || 0,
          label: paragraph6GemeindeAnteilErzeuger.generator_display_name ?? ""
        }
      ];
    } else {
      return (
        paragraph6UnassignedGenerators?.map((unassignedGenerator) => ({
          value: unassignedGenerator.generator,
          label: unassignedGenerator.generator_display_name
        })) ?? []
      );
    }
  }

  useEffect(() => {
    if (editMode || !selectedGenerator) {
      return;
    }

    setValue("anlagenschluessel_eeg", selectedGenerator.anlagenschluessel_eeg);

    if (selectedGenerator.weighting) {
      setValue("weighting", selectedGenerator.weighting);
    }

    showToast({ type: "success", message: COPIED_FROM_NOTIFICATION });
  }, [editMode, selectedGenerator, setValue, showToast]);

  useEffect(() => {
    if (submissionError) {
      setErrorsFromResponseData<Paragraph6GemeindeAnteilErzeugerPayload>(
        showErrorToast,
        submissionError,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    }
  }, [setError, submissionError, t, watch, showErrorToast]);

  return (
    <form
      className="Paragraph6GeneratorForm"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      <Tabs defaultValue={TABS[0].value}>
        <Tabs.List>
          {TABS.filter((tab) => editMode || !tab.hiddenInCreateMode).map(
            (tab) => (
              <Tabs.Tab key={tab.value} value={tab.value}>
                {tab.label}
              </Tabs.Tab>
            )
          )}
        </Tabs.List>
        <Tabs.Panel value={TABS[0].value}>
          <section>
            <FormFieldController
              control={control}
              data={generatorFieldInfo}
              error={errors.generator}
            />
            {hasBlockingError && selectedGenerator && (
              <BlockingErrorAlert
                anlagenschluesselError={anlagenschluesselError}
                installierteLeistungError={installierteLeistungMissingError}
                projectId={projectId}
                selectedGenerator={selectedGenerator}
                weightingError={weightingError}
              />
            )}
            {installierteLeistungTooLowWarning && (
              <Alert
                color={AlertPresets.warning.color}
                icon={AlertPresets.warning.icon}
                mb="sm"
              >
                {t("errors.Paragraph6.GeneratorInstalledCapacityTooLow", {
                  capacity: selectedGenerator?.installed_capacity
                })}
              </Alert>
            )}
            {!hasBlockingError && (
              <LoadOrError
                className="melomalos-loading"
                loading={meteringOrMarketLocationsLoading}
                loadingMessage="Zählpunkte werden geladen ..."
              >
                {Array.isArray(meteringOrMarketLocations) &&
                  meteringOrMarketLocations.length > 0 && (
                    <>
                      <FormFieldController
                        control={control}
                        data={
                          PARAGRAPH_6_GENERATOR_FORM_INPUT_DATA.community_share
                        }
                        error={errors.community_share}
                      />

                      <FormFieldController
                        control={control}
                        data={
                          PARAGRAPH_6_GENERATOR_FORM_INPUT_DATA.anlagenschluessel_eeg
                        }
                        error={errors.anlagenschluessel_eeg}
                      />
                    </>
                  )}
                {meteringOrMarketLocations?.length === 0 &&
                  selectedGenerator && (
                    <Alert
                      color={AlertPresets.warning.color}
                      icon={AlertPresets.warning.icon}
                    >
                      In Ihrer Konfiguration wurde für den ausgewählten Erzeuger
                      bisher kein geeichter Zähler mit Mess- oder Marktlokation
                      angelegt.
                      <br />
                      Bitte ergänzen Sie diese in Ihrer{" "}
                      <Link
                        to={`../../liegenschaften/${selectedGenerator?.site}/zaehler`}
                      >
                        Liegenschaft
                      </Link>
                      .
                    </Alert>
                  )}
              </LoadOrError>
            )}
          </section>
        </Tabs.Panel>
        <Tabs.Panel value={TABS[1].value}>
          {paragraph6GemeindeAnteilErzeuger?.contract &&
            paragraph6GemeindeAnteilErzeuger.generator && (
              <HistoryTab
                entityId={paragraph6GemeindeAnteilErzeuger.contract}
                entityType="paragraph6Generator"
                fieldNameToLabelMap={fieldNameToLabelMap}
                subEntityId={paragraph6GemeindeAnteilErzeuger.generator}
              />
            )}
        </Tabs.Panel>
      </Tabs>
      <Group justify="flex-end" my="md">
        <Button disabled={isSubmitting} variant="default" onClick={onCancel}>
          Abbrechen
        </Button>
        <Button
          color="primary"
          disabled={
            !meteringOrMarketLocations ||
            meteringOrMarketLocations?.length === 0 ||
            !!hasBlockingError
          }
          loading={isSubmitting}
          type="submit"
        >
          Speichern
        </Button>
      </Group>
    </form>
  );
}

export { Paragraph6GeneratorForm, Paragraph6GeneratorFormProps };
